// ---------------------------------------------------------------------------
// Tema claro / oscuro.
//
// `initTheme()` se corre en main.jsx ANTES de montar React para que el HTML
// arranque ya con la clase correcta (sin parpadeo). `useTheme()` expone el tema
// actual y el toggle a los botones de la navegación.
//
// Orden de decisión: localStorage ('light' | 'dark') si el usuario eligió una vez;
// si no, la preferencia del SISTEMA (prefers-color-scheme). El backoffice
// (admin.soedia.com) arranca SIEMPRE en oscuro hasta que el usuario elija claro.
// Toda elección del usuario se guarda para la próxima sesión.
// ---------------------------------------------------------------------------
import { useState } from 'react';
import { parseApp } from './tenant.js';

const THEME_KEY = 'theme';

const readSaved = () => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
};

const systemDark = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;

const isDarkFromDom = () =>
  typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

/**
 * Barra de estado / del navegador (meta theme-color) del MISMO color que el fondo de la app.
 * index.html trae dos metas por `prefers-color-scheme`, pero el tema puede elegirse a mano
 * y cada pantalla tiñe el fondo (brandPalette.applyTheme): se deja una sola meta, sin `media`,
 * con el valor actual de --canvas. Se llama al cambiar de tema o de tinte.
 */
export function syncThemeColor() {
  if (typeof document === 'undefined') return;
  const canvas = getComputedStyle(document.documentElement).getPropertyValue('--canvas').trim();
  if (!canvas) return;
  const metas = [...document.querySelectorAll('meta[name="theme-color"]')];
  let meta = metas.shift();
  metas.forEach((m) => m.remove());
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.removeAttribute('media');
  meta.content = `rgb(${canvas.split(/\s+/).join(', ')})`;
}

/**
 * Se llama UNA vez al arrancar (main.jsx). Aplica la clase `dark` al <html> según
 * localStorage o el sistema, guarda la elección implícita y sigue los cambios del
 * sistema mientras el usuario no haya fijado un tema manual.
 */
export function initTheme() {
  if (typeof document === 'undefined') return;
  const saved = readSaved();
  // Backoffice: oscuro por defecto (no sigue al sistema); el toggle del header lo cambia.
  const adminDefault = parseApp() === 'admin';
  const dark = saved ? saved === 'dark' : adminDefault || systemDark();
  document.documentElement.classList.toggle('dark', dark);
  syncThemeColor();

  // Si el usuario aún no eligió, seguimos al sistema en vivo (cambia de tema al
  // apagar el modo oscuro del SO); una vez que elige, el guardado manda.
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => {
      if (readSaved() || adminDefault) return;
      document.documentElement.classList.toggle('dark', e.matches);
      syncThemeColor();
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
  }
}

/** Estado del tema + control del toggle (sin Context: el tema vive en el <html>). */
export function useTheme() {
  const [theme, setTheme] = useState(() => (isDarkFromDom() ? 'dark' : 'light'));

  const apply = (next) => {
    document.documentElement.classList.toggle('dark', next === 'dark');
    syncThemeColor();
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      /* sin cuota o modo privado: el tema aún aplica para esta sesión */
    }
    setTheme(next);
  };

  return {
    theme,
    apply,
    toggle: () => apply(theme === 'dark' ? 'light' : 'dark'),
  };
}